import React, { useState, useEffect } from 'react'
import { signInWithPhoneNumber } from 'firebase/auth'
import { auth, setupRecaptcha, getAppCheckToken } from './firebase'
import { API_BASE } from './api'
import { fetchVippsStatus, startVippsLogin } from './vippsAuth'
import { Trophy, ScanLine, CalendarDays } from 'lucide-react'
import Logo from './components/Logo'
import { Alert, Button } from './components/ui'
import './Login.css'

function formatPhoneNumber(input) {
  if (!input || input.trim() === '') return input
  let cleaned = input.replace(/\s+/g, '').replace(/[^\d+]/g, '')
  if (cleaned.startsWith('+')) return cleaned
  if (cleaned.startsWith('47') && cleaned.length >= 10) return '+' + cleaned
  if (cleaned.startsWith('0')) {
    const withoutZero = cleaned.substring(1)
    if (withoutZero.length === 8) return '+47' + withoutZero
  }
  if (cleaned.length === 8 && /^\d{8}$/.test(cleaned)) return '+47' + cleaned
  return '+47' + cleaned
}

function Login({ onLoginSuccess }) {
  const [phoneNumber, setPhoneNumber] = useState('')
  const [verificationCode, setVerificationCode] = useState('')
  const [confirmationResult, setConfirmationResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [recaptchaReady, setRecaptchaReady] = useState(false)
  const [vippsEnabled, setVippsEnabled] = useState(false)
  const [showSmsLogin, setShowSmsLogin] = useState(false)

  useEffect(() => {
    try {
      setupRecaptcha('recaptcha-container')
      setRecaptchaReady(true)
    } catch (err) {
      setError('Firebase er ikke konfigurert. Opprett .env med REACT_APP_FIREBASE_* verdier.')
      setRecaptchaReady(false)
    }

    fetchVippsStatus(API_BASE)
      .then((status) => {
        setVippsEnabled(!!status.enabled)
        if (!status.enabled) setShowSmsLogin(true)
      })
      .catch(() => {
        setVippsEnabled(false)
        setShowSmsLogin(true)
      })
  }, [])

  const handleVippsLogin = async () => {
    setError('')
    setLoading(true)
    try {
      await startVippsLogin(API_BASE, 'partner')
    } catch (err) {
      setError(err.message || 'Kunne ikke starte Vipps-innlogging')
      setLoading(false)
    }
  }

  const handlePhoneSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      if (!recaptchaReady) throw new Error('reCAPTCHA er ikke klar')
      const formattedPhone = formatPhoneNumber(phoneNumber.trim())
      if (!formattedPhone.match(/^\+47\d{8}$/)) {
        throw new Error('Ugyldig telefonnummer. Skriv inn 8 siffer.')
      }
      const recaptcha = setupRecaptcha('recaptcha-container')
      const confirmation = await signInWithPhoneNumber(auth, formattedPhone, recaptcha)
      setConfirmationResult(confirmation)
    } catch (err) {
      setError(err.message || 'Kunne ikke sende SMS. Sjekk telefonnummeret.')
    } finally {
      setLoading(false)
    }
  }

  const handleCodeSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const result = await confirmationResult.confirm(verificationCode)
      const idToken = await result.user.getIdToken()
      localStorage.setItem('firebaseToken', idToken)

      const appCheckToken = await getAppCheckToken()
      const headers = {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      }
      if (appCheckToken) headers['X-Firebase-AppCheck'] = appCheckToken

      // Sikre at brukeren finnes i databasen.
      await fetch(`${API_BASE}/api/auth/me`, { headers })

      // Sjekk at brukeren administrerer minst ett anlegg.
      const facRes = await fetch(`${API_BASE}/api/facilities/my`, { headers })
      if (facRes.ok) {
        const facilities = await facRes.json()
        if (!facilities || facilities.length === 0) {
          setError('Du er ikke registrert som eier/admin av et anlegg. Kontakt Spelaren.')
          localStorage.removeItem('firebaseToken')
          setLoading(false)
          return
        }
      }

      onLoginSuccess(idToken)
    } catch (err) {
      setError('Ugyldig verifikasjonskode. Prøv igjen.')
    } finally {
      setLoading(false)
    }
  }

  const smsPhone = formatPhoneNumber(phoneNumber.trim())
  const showPrefix = !phoneNumber.trim().startsWith('+')

  let step
  if (confirmationResult) {
    step = (
      <form className="login-step" onSubmit={handleCodeSubmit}>
        <div className="login-step-head">
          <h2 className="login-step-title">Verifiser telefonnummer</h2>
          <p className="login-step-text">Vi sendte en kode til <b>{smsPhone}</b></p>
        </div>
        <label className="login-field">
          6-sifret kode
          <input
            className="login-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            maxLength="6"
            required
            autoFocus
          />
        </label>
        {error && <Alert tone="error">{error}</Alert>}
        <div className="login-actions">
          <Button
            variant="tertiary"
            size="lg"
            onClick={() => {
              setConfirmationResult(null)
              setVerificationCode('')
              setError('')
            }}
          >
            Tilbake
          </Button>
          <Button type="submit" size="lg" disabled={loading}>
            {loading ? 'Verifiserer…' : 'Verifiser'}
          </Button>
        </div>
      </form>
    )
  } else if (vippsEnabled && !showSmsLogin) {
    step = (
      <div className="login-step">
        <div className="login-step-head">
          <h2 className="login-step-title">Logg inn</h2>
          <p className="login-step-text">Logg inn med Vipps, eller bruk SMS som backup.</p>
        </div>
        {error && <Alert tone="error">{error}</Alert>}
        <button type="button" className="login-vipps" onClick={handleVippsLogin} disabled={loading}>
          {loading ? 'Starter Vipps…' : <>Logg inn med <b>Vipps</b></>}
        </button>
        <button
          type="button"
          className="login-link"
          onClick={() => {
            setError('')
            setShowSmsLogin(true)
          }}
        >
          Har du ikke Vipps? Logg inn med SMS
        </button>
      </div>
    )
  } else {
    step = (
      <form className="login-step" onSubmit={handlePhoneSubmit}>
        <div className="login-step-head">
          <h2 className="login-step-title">Logg inn med telefonnummer</h2>
          <p className="login-step-text">Skriv inn ditt telefonnummer for å motta en kode via SMS</p>
        </div>
        <label className="login-field">
          Telefonnummer
          <span className="login-phone">
            {showPrefix && <span className="login-phone-prefix" aria-hidden="true">+47</span>}
            <input
              type="tel"
              inputMode="tel"
              autoComplete="tel-national"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              onBlur={() => {
                if (!phoneNumber.trim()) return
                const formatted = formatPhoneNumber(phoneNumber.trim())
                // Norske nummer vises uten +47 siden prefikset står foran feltet.
                setPhoneNumber(/^\+47\d{8}$/.test(formatted) ? formatted.slice(3) : formatted)
              }}
              placeholder="123 45 678"
              required
              autoFocus
            />
          </span>
          <span className="field-hint">8 siffer. +47 legges til automatisk.</span>
        </label>
        {error && <Alert tone="error">{error}</Alert>}
        <Button type="submit" size="lg" disabled={loading || !recaptchaReady}>
          {loading ? 'Sender SMS…' : 'Send verifikasjonskode'}
        </Button>
        {!recaptchaReady && <Alert tone="warning">Initialiserer reCAPTCHA…</Alert>}
        {vippsEnabled && (
          <button type="button" className="login-link" onClick={() => setShowSmsLogin(false)}>
            Tilbake til Vipps-innlogging
          </button>
        )}
      </form>
    )
  }

  return (
    <LoginShell>
      {step}
      <div id="recaptcha-container"></div>
    </LoginShell>
  )
}

const FEATURES = [
  { icon: Trophy, title: 'Turneringer', text: 'Sett opp grupper og cup, registrer resultater og følg tabellen live.' },
  { icon: ScanLine, title: 'Salg og innløsning', text: 'Selg produkter i appen og skann QR-koden når spilleren henter varen.' },
  { icon: CalendarDays, title: 'Utleie av haller', text: 'Legg ut ledige tider i kalenderen. Følgerne booker og betaler i appen.' }
]

const Brand = ({ className }) => (
  <div className={`login-brand ${className || ''}`}>
    <Logo variant="full" color="#0F1511" height={34} />
    <span className="login-partner-tag">PARTNER</span>
  </div>
)

/* Felles ramme for innlogging og Vipps-retur. children havner i innloggingskortet. */
export function LoginShell({ children }) {
  return (
    <div className="login-shell">
      <section className="login-about" aria-label="Om partnerportalen">
        <Brand />
        <div className="login-intro">
          <span className="eyebrow">For eiere og administratorer av anlegg</span>
          <h2 className="login-hero">Arenaen din,<br />samlet på ett sted.</h2>
          <p className="login-lead">
            Partnerportalen gir deg kontroll på det som skjer i hallene: turneringer, produktsalg og utleie.
            Spillerne bruker Spelaren-appen, du styrer herfra.
          </p>
        </div>
        <ul className="login-features">
          {FEATURES.map(({ icon: Icon, title, text }) => (
            <li key={title} className="login-feature">
              <span className="login-feature-icon"><Icon size={22} strokeWidth={1.9} aria-hidden="true" /></span>
              <div className="login-feature-text">
                <span className="login-feature-title">{title}</span>
                <span className="login-feature-desc">{text}</span>
              </div>
            </li>
          ))}
        </ul>
        <span className="login-footnote">Spiller? Last ned Spelaren-appen i App Store eller Google Play.</span>
      </section>
      <section className="login-main" aria-label="Innlogging">
        <Brand className="login-mobile-brand" />
        <div className="login-card">
          <div className="login-card-head">
            <span className="eyebrow">partner.spelaren.no</span>
            <h1 className="login-card-title">Spelaren Partner</h1>
          </div>
          {children}
        </div>
      </section>
    </div>
  )
}

export default Login
